import { c as _c } from "react/compiler-runtime";
import type { OnboardingRequirementCollectDocument, OnboardingSessionConfig } from '@onefootprint/request-types';
import { createContext, useContext, useReducer } from 'react';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import type { CameraErrorCause, IdDocImageErrorType, ReceivedImagePayload } from '../../types';
import { isDenied, isGranted, isPrompt } from '../../utils/capture';
export type SelfieStep = 'init' | 'consent' | 'requestCameraAccess' | 'cameraAccessDenied' | 'cameraErrored' | 'capture' | 'processing' | 'retry' | 'complete' | 'failure';
export type SelfieCtx = {
  authToken: string;
  device: DeviceInfo;
  requirement: OnboardingRequirementCollectDocument;
  onboardingConfig: OnboardingSessionConfig;
  isConsentMissing: boolean;
  isFaceDetectionEnabled: boolean;
  cameraPermissionState: PermissionState;
  cameraErrorCause?: CameraErrorCause;
  id?: string;
  image?: ReceivedImagePayload;
  errors?: IdDocImageErrorType[];
  hasBadConnectivity?: boolean;
};
export type SelfieState = {
  value: SelfieStep;
  context: SelfieCtx;
};
export type SelfieAction = {
  type: 'contextInitialized';
  payload: {
    id: string;
    cameraPermissionState: PermissionState;
  };
} | {
  type: 'consentReceived';
} | {
  type: 'cameraAccessGranted';
  payload: {
    status: PermissionState;
  };
} | {
  type: 'cameraAccessDenied';
  payload: {
    status: PermissionState;
  };
} | {
  type: 'cameraErrored';
  payload: {
    cause: CameraErrorCause;
  };
} | {
  type: 'cameraStuck';
} | {
  type: 'navigatedToPrev';
} | {
  type: 'receivedImage';
  payload: ReceivedImagePayload;
} | {
  type: 'processingSucceeded';
} | {
  type: 'processingErrored';
  payload: {
    errors: IdDocImageErrorType[];
  };
} | {
  type: 'retakeSelfie';
} | {
  type: 'retryLimitExceeded';
};
const firstCaptureStep = (permission: PermissionState): SelfieStep => {
  if (isPrompt(permission)) return 'requestCameraAccess';
  if (isDenied(permission)) return 'cameraAccessDenied';
  return 'capture';
};
const selfieReducer = (state: SelfieState, action: SelfieAction): SelfieState => {
  const {
    value: step,
    context: ctx
  } = state;
  switch (action.type) {
    case 'contextInitialized':
      {
        if (step !== 'init') return state;
        // init re-checks camera permission live, since an upstream document node may already have it.
        const context = {
          ...ctx,
          id: action.payload.id,
          cameraPermissionState: action.payload.cameraPermissionState
        };
        if (ctx.isConsentMissing) return {
          value: 'consent',
          context
        };
        return {
          value: firstCaptureStep(action.payload.cameraPermissionState),
          context
        };
      }
    case 'consentReceived':
      {
        if (step !== 'consent') return state;
        return {
          value: firstCaptureStep(ctx.cameraPermissionState),
          context: {
            ...ctx,
            isConsentMissing: false
          }
        };
      }
    case 'cameraAccessGranted':
      {
        if (step !== 'requestCameraAccess') return state;
        const context = {
          ...ctx,
          cameraPermissionState: action.payload.status
        };
        return isGranted(action.payload.status) ? {
          value: 'capture',
          context
        } : {
          value: 'requestCameraAccess',
          context
        };
      }
    case 'cameraAccessDenied':
      return {
        value: 'cameraAccessDenied',
        context: {
          ...ctx,
          cameraPermissionState: action.payload.status
        }
      };
    case 'cameraErrored':
      return {
        value: 'cameraErrored',
        context: {
          ...ctx,
          cameraErrorCause: action.payload.cause
        }
      };
    case 'cameraStuck':
      return step === 'capture' ? {
        value: 'requestCameraAccess',
        context: ctx
      } : state;
    case 'navigatedToPrev':
      // The camera-access sub-screens fall back to the access request; consent is the flow's entry.
      if (step === 'cameraAccessDenied' || step === 'cameraErrored') {
        return {
          value: 'requestCameraAccess',
          context: ctx
        };
      }
      return state;
    case 'receivedImage':
      return step === 'capture' ? {
        value: 'processing',
        context: {
          ...ctx,
          image: action.payload
        }
      } : state;
    case 'processingSucceeded':
      return {
        value: 'complete',
        context: ctx
      };
    case 'processingErrored':
      {
        const hasBadConnectivity = action.payload.errors.some(e => e.errorType === 'network_error') || ctx.hasBadConnectivity;
        // Show the rejection reason before the user retakes.
        return {
          value: 'retry',
          context: {
            ...ctx,
            errors: action.payload.errors,
            image: undefined,
            hasBadConnectivity
          }
        };
      }
    case 'retakeSelfie':
      return step === 'retry' ? {
        value: 'capture',
        context: {
          ...ctx,
          errors: undefined
        }
      } : state;
    case 'retryLimitExceeded':
      return {
        value: 'failure',
        context: ctx
      };
    default:
      return state;
  }
};
type SelfieContextValue = [SelfieState, React.Dispatch<SelfieAction>];
const SelfieContext = createContext<SelfieContextValue | undefined>(undefined);
type SelfieProviderProps = {
  initialContext: SelfieCtx;
  children: React.ReactNode;
};
const SelfieProvider = t0 => {
  const $ = _c(5);
  const {
    initialContext,
    children
  } = t0;
  let t1;
  if ($[0] !== initialContext) {
    t1 = {
      value: "init",
      context: initialContext
    };
    $[0] = initialContext;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const value = useReducer(selfieReducer, t1);
  let t2;
  if ($[2] !== children || $[3] !== value) {
    t2 = <SelfieContext.Provider value={value}>{children}</SelfieContext.Provider>;
    $[2] = children;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const useSelfieMachine = () => {
  const ctx = useContext(SelfieContext);
  if (!ctx) {
    throw new Error("useSelfieMachine must be used inside SelfieProvider");
  }
  return ctx;
};
export { SelfieProvider, useSelfieMachine };